import Service from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { Sound } from "#models/sound.ts";
import spriteUrl from "#app/assets/audio/sprite.mp3";
import type Owner from "@ember/owner";
import { isTesting } from "@embroider/macros";

/**
 * Service for playing audio samples from the loaded sounds mp3. It uses the Web Audio API.
 */
export default class AudioService extends Service {
  @tracked isLoaded = false;

  audioContext: AudioContext | null = null;
  audioBuffer: AudioBuffer | null = null;
  masterGainNode: GainNode | null = null;
  loadPromise: Promise<void> | null = null;

  constructor(owner?: Owner) {
    super(owner);
    if (!isTesting() && typeof window !== "undefined") {
      void this.loadAudio();
    }
  }

  setAudioContext = (context: AudioContext): void => {
    this.audioContext = context;
    this.masterGainNode = null;
  };

  /**
   * Returns the master gain node, creating and connecting it to destination if needed.
   */
  getMasterGainNode = (): GainNode => {
    if (!this.masterGainNode) {
      const context = this.getContext();
      this.masterGainNode = context.createGain();
      this.masterGainNode.connect(context.destination);
    }
    return this.masterGainNode;
  };

  /**
   * Sets the decoded audio buffer and marks the service as loaded.
   */
  setAudioBuffer = (buffer: AudioBuffer): void => {
    this.audioBuffer = buffer;
    this.isLoaded = true;
  };

  /**
   * Returns the active Web Audio context, creating or resuming it if necessary. We use this
   * because the browser starts the audio context as suspended and will only resume it as
   * the result of user interaction.
   */
  getContext = (): AudioContext => {
    if (!this.audioContext) {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioContext = new AudioCtx();
    }
    if (this.audioContext.state === "suspended") {
      void this.audioContext.resume();
    }
    return this.audioContext;
  };

  /**
   * Asynchronously fetches and decodes the audio sprite file into memory.
   */
  loadAudio = async (): Promise<void> => {
    if (this.isLoaded) {
      return;
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = (async () => {
      try {
        const context = this.getContext();
        const response = await fetch(spriteUrl);
        const arrayBuffer = await response.arrayBuffer();
        const buffer = await context.decodeAudioData(arrayBuffer);
        this.setAudioBuffer(buffer);
      } catch (err) {
        console.error("Failed to load audio sprite:", err);
      } finally {
        this.loadPromise = null;
      }
    })();

    return this.loadPromise;
  };

  /**
   * Plays a specified sound sprite with customizable volume and start time scheduling.
   */
  playSound = (
    sound: Sound,
    volume: number = 1.0,
    scheduledTime?: number,
    isRetry = false,
  ): AudioBufferSourceNode | null => {
    if (volume <= 0) return null;

    if (!this.audioBuffer || !this.isLoaded) {
      return null;
    }

    const context = this.getContext();

    if (context.state === "suspended") {
      const resumeResult = context.resume();
      if (scheduledTime === undefined && !isRetry) {
        void Promise.resolve(resumeResult).then(() => {
          this.playSound(sound, volume, scheduledTime, true);
        });
        return null;
      }
    }

    const { offset, duration } = sound;
    const sourceNode = context.createBufferSource();
    sourceNode.buffer = this.audioBuffer;

    const gainNode = context.createGain();
    const normalizedVolume = Math.max(0, Math.min(1, volume));
    const timeToPlay = Math.max(scheduledTime ?? 0, context.currentTime);

    if (gainNode.gain && typeof gainNode.gain.setValueAtTime === "function") {
      gainNode.gain.setValueAtTime(normalizedVolume, timeToPlay);
    } else {
      gainNode.gain.value = normalizedVolume;
    }

    sourceNode.connect(gainNode);
    gainNode.connect(this.getMasterGainNode());

    sourceNode.start(timeToPlay, offset, duration);

    return sourceNode;
  };
}

declare module "@ember/service" {
  interface Registry {
    audio: AudioService;
  }
}
